import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import InstaTaskSection from 'components/common/InstaTaskSection';
import { servicePointsServices } from '@services';
import { showError } from '@utils';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { usePermissions, useServicePoints } from '@hooks';
import PartnershipDetailsForm from './PartnershipDetailsForm';
import styles from './partnershipDetails.style';

const PartnershipDetails = ({
  servicePoint,
  getServicePoint,
  selectedTabOption,
}) => {
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const { invalidateServicePoints } = useServicePoints({ enabled: false });
  const info = servicePoint?.partnershipInfo || {};
  const defaultValues = {
    workHourCharge: info.workHourCharge || '',
    workDiscount: info.workDiscount || '',
    sparePartsDiscount: info.sparePartsDiscount || '',
    paymentTerms: info.paymentTerms || '',
    replacementVehicle: info.replacementVehicle || false,
    temporaryVehicleParking: info.temporaryVehicleParking || false,
    carsPerDay: info.carsPerDay || '',
    availableAppointments: info.availableAppointments || '',
    technicalStaff: info.technicalStaff || '',
    isCertified: info.isCertified || false,
  };

  const methods = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (info) {
      methods.reset(defaultValues);
    }
  }, [info]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  const { findEntity } = usePermissions();
  if (selectedTabOption !== 'partnershipDetails') return null;

  const canWrite = findEntity([ENTITIES_PERMISIONS.WriteServicePoints]);

  const computedPayload = (data) => {
    return {
      partnershipInfo: {
        availableAppointments: Number(data?.availableAppointments),
        carsPerDay: Number(data?.carsPerDay),
        paymentTerms: Number(data?.paymentTerms),
        replacementVehicle: data?.replacementVehicle,
        sparePartsDiscount: Number(data?.sparePartsDiscount),
        technicalStaff: Number(data?.technicalStaff),
        temporaryVehicleParking: data?.temporaryVehicleParking,
        workDiscount: Number(data?.workDiscount),
        workHourCharge: Number(data?.workHourCharge),
        isCertified: data?.isCertified,
      },
    };
  };

  const onSubmit = async (form) => {
    const payload = computedPayload(form);

    try {
      setLoading(true);
      await servicePointsServices.editServicePoint(servicePoint?.id, payload);
      invalidateServicePoints();
      await getServicePoint();
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (e) => {
    e.stopPropagation();
    if (info) {
      methods.reset(defaultValues);
    }
  };

  const handleSubmit = (e) => {
    e.stopPropagation();
    methods.handleSubmit(onSubmit)();
  };

  return (
    <InstaTaskSection
      title="Partnership Details"
      defaultExpanded
      hideActions={!canWrite}
      secondaryAction={{ label: 'Reset', onClick: handleReset }}
      primaryAction={{ label: 'OK', onClick: handleSubmit }}
      onToggle={toggleExpanded}
    >
      <Box sx={{ position: 'relative' }}>
        <PartnershipDetailsForm
          methods={methods}
          loading={loading}
          canWrite={canWrite}
          onSubmit={onSubmit}
        />

        {loading && (
          <Box sx={styles.loadingWrapper}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </InstaTaskSection>
  );
};

const memoizedPartnershipDetails = React.memo(PartnershipDetails);
export { memoizedPartnershipDetails as PartnershipDetails };

export default memoizedPartnershipDetails;
